import { type HTMLAttributes, type ReactNode } from "react"

import { IconButton, type ButtonSize, type IconButtonProps } from "~/components/reusable/Button"
import { Tooltip } from "~/components/reusable/Tooltip"
import { cn } from "~/utils/helpers"

/** The hover-reveal label pill's chrome, shared with the gate's bubble so a gated item's single
 * tooltip looks like the rest of the bar's. */
const PILL_CLASS = "rounded-[10px] border border-base"

type IconToolbarItemProps = {
  icon: ReactNode
  /** Hover-reveal label pill text, and the button's accessible name. */
  label: string
  /** Toggle on/off. Its selected styling stays hidden until hover ("silent" resting). */
  active?: boolean
  /** Make this item the toolbar's primary action: emphasized (filled) at rest instead of
   *  silent. Still icon-only with a hover-reveal label. At most one per toolbar. */
  cta?: boolean
  size?: ButtonSize
} & Omit<IconButtonProps, "children" | "aria-label" | "size" | "variant">

function IconToolbarItem({
  icon,
  label,
  active,
  cta,
  size = "base",
  className,
  requires,
  allowedTitle,
  tooltipSide = "top",
  ...props
}: IconToolbarItemProps) {
  const button = (
    <IconButton
      variant={cta ? "primary" : "transparent"}
      size={size}
      aria-label={label}
      aria-pressed={active}
      className={cn(
        "transition-colors duration-150",
        !cta && active && "hover:bg-highlight hover:[&_svg]:fill-fg-interactive",
        className,
      )}
      requires={requires}
      allowedTitle={allowedTitle ?? label}
      tooltipSide={tooltipSide}
      tooltipClassName={PILL_CLASS}
      {...props}
    >
      {icon}
    </IconButton>
  )

  // A gated item already renders one tooltip of its own — the permission reason while blocked,
  // the label while allowed — so the hover-reveal pill would be a second bubble on the same
  // control, and both open at once.
  if (requires !== undefined) return button

  return (
    <Tooltip title={label} side="top" className={PILL_CLASS}>
      {button}
    </Tooltip>
  )
}

/**
 * Icon toolbar with hover-reveal labels — the resting bar is silent (no labels, no
 * visible selection); hovering an item reveals its label pill (above the bar) and any
 * active styling. `aria-pressed` keeps the active toggle exposed to assistive tech.
 * At most one item may set `cta` to render emphasized (filled) at rest as the bar's
 * primary action. An item that triggers a mutation takes `requires`, and then owns its single
 * tooltip rather than the hover-reveal pill.
 * Canonical intent: {@link ../design-system/intents.ts ICON_TOOLBAR_INTENT}.
 * Approved surface: design-system/APPROVED.md.
 */
function IconToolbar({ children, className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div role="toolbar" className={cn("inline-flex items-center gap-1", className)} {...props}>
      {children}
    </div>
  )
}

export { IconToolbar, IconToolbarItem }
export type { IconToolbarItemProps }
